import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import * as personaService from '../../services/personaService';

interface PersonaPickerProps {
  selectedPersonaId?: string;
  onPersonaSelect: (persona: any) => void;
  onClose: () => void;
  showSearch?: boolean;
  placeholder?: string;
}

export const PersonaPicker: React.FC<PersonaPickerProps> = ({
  selectedPersonaId,
  onPersonaSelect,
  onClose,
  showSearch = true,
  placeholder = "Search personas..."
}) => {
  const { darkMode } = useTheme();
  const [personas, setPersonas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load personas on mount
  useEffect(() => {
    const loadPersonas = async () => {
      console.log('🗂️ PERSONA-PICKER: Starting to load personas');
      try {
        setLoading(true);
        setError(null);
        const personasData = await personaService.getPersonas();
        console.log('🗂️ PERSONA-PICKER: Loaded personas, count:', personasData.length);
        
        // Format personas for consistent structure
        const formattedPersonas = personasData.map(persona => ({
          id: persona.id,
          name: persona.name,
          shortDescription: persona.shortDescription || persona.primarySpecialization || 'General purpose',
          primarySpecialization: persona.primarySpecialization || 'General',
          tags: Array.isArray(persona.tags) ? persona.tags : [],
        }));
        
        setPersonas(formattedPersonas);
        console.log('🗂️ PERSONA-PICKER: Set personas state, this might trigger updates');
      } catch (err) {
        console.error('🗂️ PERSONA-PICKER: Error loading personas:', err);
        setError('Failed to load personas');
      } finally {
        setLoading(false);
      }
    };

    loadPersonas();
  }, []);

  // Focus search input on mount
  useEffect(() => {
    if (searchInputRef.current && showSearch) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Filter personas
  const filteredPersonas = useMemo(() => {
    let filtered = personas;

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(term) ||
        p.shortDescription.toLowerCase().includes(term) ||
        p.primarySpecialization.toLowerCase().includes(term)
      );
    }

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [personas, searchTerm]);

  // Handle persona selection
  const handlePersonaSelect = (persona: any) => {
    console.log('🎯 PICKER-DEBUG: handlePersonaSelect called with persona:', persona);
    console.log('🎯 PICKER-DEBUG: onPersonaSelect prop type:', typeof onPersonaSelect);
    console.log('🎯 PICKER-DEBUG: onPersonaSelect prop value:', onPersonaSelect);
    
    // Log the call stack to see where this is coming from
    console.trace('🎯 PICKER-DEBUG: Call stack for handlePersonaSelect');
    
    onPersonaSelect(persona);
    onClose();
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
      style={{ zIndex: 999999 }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={containerRef}
        className={`w-full max-w-4xl mx-4 rounded-lg shadow-2xl border ${
          darkMode 
            ? 'border-gray-600 bg-gray-800 text-white' 
            : 'border-gray-300 bg-white text-gray-900'
        }`}
        style={{
          maxHeight: '80vh',
          height: '600px',
          minWidth: '800px',
        }}
      >
        {/* Header */}
        <div className={`p-4 border-b ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        } flex items-center justify-between`}>
          <h3 className={`text-lg font-semibold ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Select Persona
          </h3>
          <button
            onClick={onClose}
            className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
              darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            ✕
          </button>
        </div>

        {/* Search */}
        {showSearch && (
          <div className={`p-4 border-b ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-3 py-2 rounded border ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>
        )}

        {/* Content - Simple Table/List */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Loading personas...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">
              <p>{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Retry
              </button>
            </div>
          ) : filteredPersonas.length === 0 ? (
            <div className="p-8 text-center">
              <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                No personas found matching your search
              </p>
            </div>
          ) : (
            <div className="h-full overflow-y-auto">
              {/* Table Header */}
              <div className={`sticky top-0 grid grid-cols-4 gap-6 p-4 border-b font-medium text-sm ${
                darkMode 
                  ? 'bg-gray-800 border-gray-700 text-gray-300' 
                  : 'bg-gray-50 border-gray-200 text-gray-700'
              }`}>
                <div>Name</div>
                <div>Specialization</div>
                <div>Description</div>
                <div>Tags</div>
              </div>

              {/* Table Rows */}
              {filteredPersonas.map(persona => (
                <div
                  key={persona.id}
                  className={`grid grid-cols-4 gap-6 p-4 border-b cursor-pointer transition-colors ${
                    selectedPersonaId === persona.id
                      ? darkMode
                        ? 'bg-blue-900/30 border-blue-700'
                        : 'bg-blue-50 border-blue-200'
                      : darkMode
                        ? 'border-gray-700 hover:bg-gray-700/50'
                        : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handlePersonaSelect(persona);
                  }}
                >
                  <div className={`font-medium ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {persona.name}
                    {selectedPersonaId === persona.id && (
                      <span className="ml-2 text-blue-500">✓</span>
                    )}
                  </div>
                  <div className={`text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {persona.primarySpecialization}
                  </div>
                  <div className={`text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {persona.shortDescription}
                  </div>
                  <div className={`text-xs ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {persona.tags?.slice(0, 3).join(', ')}
                    {persona.tags?.length > 3 && ` +${persona.tags.length - 3}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        } text-sm ${
          darkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>
          {filteredPersonas.length} persona{filteredPersonas.length !== 1 ? 's' : ''} available
        </div>
      </div>
    </div>
  );
};
